import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import NewProjectDialog, { type NewProjectInput } from '../components/home/NewProjectDialog'
import { deleteProject, listProjects, saveProject } from '../lib/persistence'
import { createNewProject, useEditorStore } from '../store/editorStore'
import type { ProjectSummary } from '../types/project'
import BrandMark from '../components/BrandMark'

export default function Home() {
  const navigate = useNavigate()
  const loadProject = useEditorStore((s) => s.loadProject)
  const [showDialog, setShowDialog] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [projects, setProjects] = useState<ProjectSummary[]>([])

  useEffect(() => {
    listProjects().then(setProjects)
  }, [])

  async function handleCreate(input: NewProjectInput) {
    const project = createNewProject(input)
    await saveProject(project)
    loadProject(project)
    navigate(`/editor/${project.id}`)
  }

  async function handleDelete(id: string) {
    await deleteProject(id)
    setProjects(await listProjects())
  }

  return (
    <div className="flex h-full flex-col items-center overflow-y-auto bg-[var(--color-canvas)] px-6 py-16">
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-3">
          <BrandMark size={40} />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Diseña3D</h1>
            <p className="text-sm text-[var(--color-ink-soft)]">Diseña tu espacio antes de construirlo.</p>
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <button
            onClick={() => setShowDialog(true)}
            className="rounded-lg bg-[var(--color-accent)] px-5 py-3 text-sm font-medium text-white hover:opacity-90"
          >
            + Crear nuevo proyecto
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="rounded-lg border border-[var(--color-border)] px-5 py-3 text-sm text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-muted)]"
          >
            Ayuda
          </button>
        </div>

        <div className="mt-12">
          <h2 className="text-sm font-medium uppercase tracking-wide text-[var(--color-ink-soft)]">
            Proyectos recientes
          </h2>
          {projects.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--color-ink-soft)]">
              Todavía no tienes proyectos guardados en este navegador. Crea uno nuevo para comenzar.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
              {projects.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-4 py-3">
                  <button
                    className="flex-1 text-left"
                    onClick={() => navigate(`/editor/${p.id}`)}
                  >
                    <div className="text-sm font-medium text-[var(--color-ink)]">{p.name}</div>
                    <div className="text-xs text-[var(--color-ink-soft)]">
                      {p.propertyType} · {p.floorsCount} piso(s) · actualizado {new Date(p.updatedAt).toLocaleString()}
                    </div>
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="ml-3 rounded-md px-2 py-1 text-xs text-[var(--color-danger)] hover:bg-red-50"
                  >
                    Eliminar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showDialog && <NewProjectDialog onCancel={() => setShowDialog(false)} onCreate={handleCreate} />}
      {showHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="w-[480px] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Cómo empezar</h2>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-[var(--color-ink-soft)]">
              <li>Crea un proyecto e indica el tipo de propiedad y sus dimensiones generales.</li>
              <li>Usa la herramienta "Habitación" para dibujar el primer espacio arrastrando un rectángulo.</li>
              <li>Añade puertas y ventanas haciendo clic sobre un muro existente.</li>
              <li>Abre la biblioteca de muebles y decoración desde la barra izquierda para amueblar el espacio.</li>
              <li>Alterna entre la vista 2D y 3D en cualquier momento desde la barra superior.</li>
              <li>Guarda el proyecto con el botón "Guardar" o Ctrl/Cmd+S.</li>
            </ol>
            <button
              onClick={() => setShowHelp(false)}
              className="mt-5 w-full rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
